import { ChevronDown, ChevronUp, HelpCircle } from "lucide-react";
import { useState } from "react";
import { InlineReferenceMarkdown } from "@/components/ui/inline-reference-markdown";
import { MessageMarkdown } from "@/components/ui/message-markdown";
import { cn } from "@/lib/utils";
import type {
  AssistantAskUserAnswerRuntime,
  AssistantAskUserQuestionRuntime,
  AssistantAskUserTimelineItem,
} from "../types";

function getAnswerText(
  question: AssistantAskUserQuestionRuntime,
  answer: AssistantAskUserAnswerRuntime | undefined
): { text: string; markdown: boolean } {
  if (!answer) {
    return { text: "No answer recorded", markdown: false };
  }
  if (answer.kind === "custom") {
    return { text: answer.text, markdown: true };
  }
  return {
    text:
      question.options.find((option) => option.id === answer.optionId)?.label ?? "Selected option",
    markdown: false,
  };
}

function getStatusLabel(status: AssistantAskUserTimelineItem["status"]) {
  switch (status) {
    case "answered":
      return "Answered";
    case "dismissed":
      return "Dismissed questions form";
    case "expired":
      return "Expired questions form";
    default:
      // "cancelled": the run was stopped out from under the form.
      return "Closed questions form";
  }
}

/** Why a question carries no answer. Each unanswered status closed the form for its own reason. */
function getUnansweredText(status: AssistantAskUserTimelineItem["status"]) {
  switch (status) {
    case "dismissed":
      return "User dismissed";
    case "expired":
      return "Not answered before the question expired";
    default:
      return "Not answered before the run was stopped";
  }
}

function getSummaryLabel(item: AssistantAskUserTimelineItem, statusLabel: string) {
  if (item.status !== "answered") {
    return statusLabel;
  }
  return `Answered ${item.questions.length} ${item.questions.length === 1 ? "question" : "questions"}`;
}

function getVisibleAnswerText(text: string, markdown: boolean) {
  if (!markdown) {
    return text;
  }

  return text
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/`{1,3}([^`]+)`{1,3}/g, "$1")
    .replace(/[*_~>#]/g, "")
    .replace(/^\s*[-+]\s+/gm, "")
    .replace(/\s+/g, " ")
    .trim();
}

function AnswerText({ text, markdown }: { text: string; markdown: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const canExpand = getVisibleAnswerText(text, markdown).length > 96;
  const answerClassName =
    "min-w-0 max-w-full text-[13px] leading-normal text-foreground break-words";

  if (!canExpand) {
    if (markdown) {
      return (
        <MessageMarkdown
          content={text}
          className={cn(answerClassName, "leading-normal [&_p]:mb-1 [&_p:last-child]:mb-0")}
        />
      );
    }

    // A selected option's label is plain text, but it can carry a mention the model wrote
    // into the option — it must read as a chip here too, never as raw link markdown.
    return (
      <p className={answerClassName}>
        <InlineReferenceMarkdown content={text} />
      </p>
    );
  }

  return (
    <div className="flex min-w-0 max-w-full items-start gap-1.5 overflow-hidden">
      <div
        className={cn(
          answerClassName,
          "min-w-0 flex-1 basis-0 overflow-hidden transition-[max-height] duration-200 ease-out",
          expanded
            ? "max-h-96 whitespace-pre-wrap"
            : "max-h-5 [&_.message-markdown>*:not(:first-child)]:hidden [&_.message-markdown_p]:truncate [&_.message-markdown_p]:whitespace-nowrap"
        )}
        data-testid="assistant-ask-user-answer-text"
      >
        {markdown ? (
          <MessageMarkdown
            content={text}
            className="leading-normal [&_p]:mb-1 [&_p:last-child]:mb-0"
          />
        ) : (
          <InlineReferenceMarkdown content={text} />
        )}
      </div>
      <button
        type="button"
        className="ml-auto mt-0.5 flex size-4 shrink-0 items-center justify-center text-neutral-dim transition-colors hover:text-muted-foreground"
        aria-expanded={expanded}
        aria-label={expanded ? "Collapse answer" : "Expand answer"}
        onClick={() => setExpanded((current) => !current)}
      >
        {expanded ? (
          <ChevronUp className="size-[13px]" aria-hidden="true" />
        ) : (
          <ChevronDown className="size-[13px]" aria-hidden="true" />
        )}
      </button>
    </div>
  );
}

export function AssistantAskUserAnswerDetails({
  status,
  questions,
  answers,
}: {
  status: AssistantAskUserTimelineItem["status"];
  questions: AssistantAskUserQuestionRuntime[];
  answers: AssistantAskUserTimelineItem["answers"];
}) {
  return (
    <div className="flex min-w-0 max-w-full flex-col gap-2.5 overflow-hidden">
      {questions.map((question) => {
        const answer =
          status === "answered"
            ? getAnswerText(question, answers?.[question.id])
            : { text: getUnansweredText(status), markdown: false };

        return (
          <div key={question.id} className="grid min-w-0 max-w-full gap-px overflow-hidden">
            <InlineReferenceMarkdown
              content={question.question}
              className="min-w-0 max-w-full text-[13px] font-normal leading-normal text-muted-foreground break-words [&_p]:mb-0"
            />
            <AnswerText text={answer.text} markdown={answer.markdown} />
          </div>
        );
      })}
    </div>
  );
}

export function AssistantAskUserSummary({ item }: { item: AssistantAskUserTimelineItem }) {
  const summaryLabel = getSummaryLabel(item, getStatusLabel(item.status));

  return (
    <section
      className="flex w-full min-w-0 max-w-full gap-3 overflow-hidden text-foreground"
      data-testid="assistant-ask-user-summary"
    >
      <div className="flex h-6 w-6 shrink-0 items-center justify-center bg-surface-raised dark:bg-transparent">
        <HelpCircle className="size-3.5 text-primary" aria-hidden="true" />
      </div>
      <div className="min-w-0 max-w-full flex-1 overflow-hidden">
        <div className="flex h-6 min-w-0 max-w-full items-center overflow-hidden">
          <p className="min-w-0 truncate text-[13px] font-medium leading-none text-muted-foreground">
            {summaryLabel}
          </p>
        </div>
        <div className="mt-2.5">
          <AssistantAskUserAnswerDetails
            status={item.status}
            questions={item.questions}
            answers={item.answers}
          />
        </div>
      </div>
    </section>
  );
}
